import React, {useRef} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {actions, selectors} from './connectors';
import renderInlineSvg from '~/utils/renderInlineSvg';
import {rightArrowIcon} from '~/constants/InlineSvgs';
import ColorCount from './ColorCount';
import './index.scss';
import {useNavigate} from 'react-router-dom';

const getQuoteError = ({itemPrice}) => {
  return !itemPrice;
};

const getItemPrice = ({itemPrice}, quoteError) => {
  return quoteError ? '--' : parseFloat(itemPrice).toFixed(2);
};

const getItemBelowMinimum = ({
  colorMinimum,
  productMinimum,
  printMinimum,
  totalQuantity
}) => {
  return (
    colorMinimum > totalQuantity ||
    productMinimum > totalQuantity ||
    printMinimum?.printMinimumQuantity > totalQuantity
  );
};

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
export function RealTimePrice(props) {
  const {hasQuantitiesForAllProducts, totalQuantity} = props;
  const navigate = useNavigate();

  const rtpRef = useRef();

  const quoteError = getQuoteError(props);
  const itemPrice = getItemPrice(props, quoteError);
  const itemBelowMinimum = getItemBelowMinimum(props);

  const renderColorCountOrErrorMessage = () => {
    if (quoteError) {
      if (itemBelowMinimum) {
        return 'Minimum quantity not met';
      } else {
        return 'Price Not Available';
      }
    } else {
      return <ColorCount />;
    }
  };

  const handleClick = () => {
    const navigatePath =
      hasQuantitiesForAllProducts && !quoteError
        ? '/next/summary'
        : '/next/quote';
    props.onClick();
    navigate(navigatePath);
  };

  const handleChange = evt => {
    evt.stopPropagation();
    props.onClick();
  };

  const renderTotalOrErrorMessage = () => {
    if (quoteError) {
      if (quoteError && itemBelowMinimum) {
        return (
          <a href="#/next/quote" onClick={handleChange}>
            Add More
          </a>
        );
      } else {
        return (
          <a href="#/next/quote" onClick={handleChange}>
            Review Details
          </a>
        );
      }
    } else {
      return (
        <a href="#/next/quote" onClick={handleChange}>
          {totalQuantity} total items
        </a>
      );
    }
  };

  return (
    <div ref={rtpRef} className="ndx-RealTimePrice" onClick={handleClick}>
      <div className="ndx-RealTimePrice-price">
        ${itemPrice}
        <span className="ndx-RealTimePrice-price-slash">/</span>ea
      </div>
      <div className="ndx-RealTimePrice-colors">
        {renderColorCountOrErrorMessage()}
      </div>
      <div
        className={
          !quoteError ? 'ndx-RealTimePrice-total' : 'ndx-RealTimePrice-error'
        }>
        {renderTotalOrErrorMessage()}
        <span
          className="ndx-RealTimePrice-info"
          dangerouslySetInnerHTML={renderInlineSvg(rightArrowIcon)}
        />
      </div>
    </div>
  );
}

const propTypes = {
  colorMinimum: PropTypes.number,
  hasQuantitiesForAllProducts: PropTypes.bool,
  item: PropTypes.object,
  itemizedPrices: PropTypes.object,
  itemPrice: PropTypes.number,
  printMinimum: PropTypes.object,
  productMinimum: PropTypes.number,
  quantities: PropTypes.object,
  selectedQuantity: PropTypes.number,
  totalQuantity: PropTypes.number,
  viewColors: PropTypes.object,
  onClick: PropTypes.func
};

RealTimePrice.propTypes = propTypes;

RealTimePrice.defaultProps = {
  colorSelections: [],
  selectedQuantity: 0,
  totalQuantity: 0,
  viewColors: {front: 0},
  onClick() {}
};

export default connect(selectors, actions)(RealTimePrice);
